import { ListingForm as ListingFormType } from '@/types';

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  warnings: Record<string, string>;
}

export interface SectionValidation {
  isComplete: boolean;
  isValid: boolean;
  errors: string[];
  warnings: string[];
  completionPercentage: number;
}

export class ListingFormValidator {
  static validateBasicInfo(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!formData.title?.trim()) {
      errors.push('Property title is required');
    } else if (formData.title.length < 10) {
      warnings.push('Title should be at least 10 characters for better appeal');
    } else if (formData.title.length > 100) {
      errors.push('Title must be 100 characters or less');
    }

    if (!formData.description?.trim()) {
      errors.push('Property description is required');
    } else if (formData.description.length < 50) {
      warnings.push('Description should be at least 50 characters for better guest understanding');
    } else if (formData.description.length > 2000) {
      errors.push('Description must be 2000 characters or less');
    }

    if (!formData.propertyType) {
      errors.push('Property type is required');
    }

    if (!formData.roomType) {
      errors.push('Room type is required');
    }

    const requiredFields = 4;
    const completedFields = [
      formData.title?.trim(),
      formData.description?.trim(),
      formData.propertyType,
      formData.roomType,
    ].filter(Boolean).length;

    return {
      isComplete: errors.length === 0 && completedFields === requiredFields,
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage: (completedFields / requiredFields) * 100,
    };
  }

  static validateLocation(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if address object exists
    if (!formData.address) {
      errors.push('Address information is required');
      return {
        isComplete: false,
        isValid: false,
        errors,
        warnings,
        completionPercentage: 0,
      };
    }

    // Required address fields
    if (!formData.address.street?.trim()) {
      errors.push('Street address is required');
    }

    if (!formData.address.city?.trim()) {
      errors.push('City is required');
    }

    if (!formData.address.state?.trim()) {
      errors.push('State/Province is required');
    }

    if (!formData.address.zipCode?.trim()) {
      errors.push('ZIP/Postal code is required');
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.address.zipCode) && formData.address.country === 'United States') {
      warnings.push('ZIP code format may be invalid for US addresses');
    }

    if (!formData.address.country?.trim()) {
      errors.push('Country is required');
    }

    // Optional but recommended
    if (!formData.address.neighborhood?.trim()) {
      warnings.push('Adding neighborhood information helps guests understand the location');
    }

    // Coordinate validation
    if (formData.address.latitude !== undefined) {
      if (formData.address.latitude < -90 || formData.address.latitude > 90) {
        errors.push('Latitude must be between -90 and 90 degrees');
      }
    }

    if (formData.address.longitude !== undefined) {
      if (formData.address.longitude < -180 || formData.address.longitude > 180) {
        errors.push('Longitude must be between -180 and 180 degrees');
      }
    }

    const requiredFields = 5; // street, city, state, zipCode, country
    const completedFields = [
      formData.address.street?.trim(),
      formData.address.city?.trim(),
      formData.address.state?.trim(),
      formData.address.zipCode?.trim(),
      formData.address.country?.trim(),
    ].filter(Boolean).length;

    return {
      isComplete: errors.length === 0 && completedFields === requiredFields,
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage: (completedFields / requiredFields) * 100,
    };
  }

  static validateCapacity(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate guest capacity
    if (!formData.maxGuests || formData.maxGuests < 1) {
      errors.push('Must accommodate at least 1 guest');
    } else if (formData.maxGuests > 50) {
      warnings.push('Very high guest capacity - ensure this is accurate');
    }

    // Validate bedrooms
    if (!formData.bedrooms || formData.bedrooms < 0) {
      errors.push('Number of bedrooms must be 0 or greater');
    } else if (formData.bedrooms > 20) {
      warnings.push('Very high bedroom count - ensure this is accurate');
    }

    // Validate bathrooms
    if (!formData.bathrooms || formData.bathrooms < 0.5) {
      errors.push('Must have at least 0.5 bathrooms');
    } else if (formData.bathrooms > 20) {
      warnings.push('Very high bathroom count - ensure this is accurate');
    }

    // Validate beds
    if (formData.beds !== undefined && formData.beds < 0) {
      errors.push('Number of beds cannot be negative');
    } else if (formData.beds && formData.beds > 50) {
      warnings.push('Very high bed count - ensure this is accurate');
    }

    // Logic validation
    if (formData.maxGuests && formData.beds && formData.beds > 0) {
      if (formData.maxGuests > formData.beds * 2) {
        warnings.push('Guest capacity seems high compared to number of beds');
      }
    }

    if (formData.bedrooms && formData.bathrooms) {
      if (formData.bedrooms > 3 && formData.bathrooms < 2) {
        warnings.push('Consider if bathroom count is sufficient for bedroom count');
      }
    }

    const requiredFields = 3; // maxGuests, bedrooms, bathrooms
    const completedFields = [
      formData.maxGuests && formData.maxGuests > 0,
      formData.bedrooms !== undefined && formData.bedrooms >= 0,
      formData.bathrooms && formData.bathrooms >= 0.5,
    ].filter(Boolean).length;

    return {
      isComplete: errors.length === 0 && completedFields === requiredFields,
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage: (completedFields / requiredFields) * 100,
    };
  }

  static validateAmenities(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Amenities are optional but recommended
    if (!formData.amenities || formData.amenities.length === 0) {
      warnings.push('Adding amenities helps guests understand what\'s included');
    } else if (formData.amenities.length > 20) {
      warnings.push('Consider if all selected amenities are actually available');
    }

    // Safety features validation
    if (!formData.safetyFeatures || formData.safetyFeatures.length === 0) {
      warnings.push('Adding safety features increases guest confidence');
    }

    // Check for essential safety items
    const essentialSafety = ['smoke_detector', 'carbon_monoxide_detector'];
    const hasSmokeDetector = formData.safetyFeatures?.includes('smoke_detector');
    const hasCODetector = formData.safetyFeatures?.includes('carbon_monoxide_detector');

    if (!hasSmokeDetector) {
      warnings.push('Smoke detector is highly recommended for guest safety');
    }

    if (!hasCODetector) {
      warnings.push('Carbon monoxide detector is recommended for guest safety');
    }

    // This section is mostly optional, so completion is based on having some content
    const hasAmenities = formData.amenities && formData.amenities.length > 0;
    const hasSafetyFeatures = formData.safetyFeatures && formData.safetyFeatures.length > 0;
    const hasAccessibilityInfo = formData.accessibility && Object.values(formData.accessibility).some(Boolean);

    const completionPercentage = [hasAmenities, hasSafetyFeatures, hasAccessibilityInfo]
      .filter(Boolean).length * 33.33;

    return {
      isComplete: true, // This section is optional
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage: Math.min(completionPercentage, 100),
    };
  }

  static validatePricing(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if pricing object exists
    if (!formData.pricing) {
      errors.push('Pricing information is required');
      return {
        isComplete: false,
        isValid: false,
        errors,
        warnings,
        completionPercentage: 0,
      };
    }

    // Base price validation
    if (!formData.pricing.basePrice || formData.pricing.basePrice <= 0) {
      errors.push('Base price must be greater than 0');
    } else if (formData.pricing.basePrice < 10) {
      warnings.push('Very low base price - ensure this is competitive');
    } else if (formData.pricing.basePrice > 10000) {
      warnings.push('Very high base price - ensure this is accurate');
    }

    // Currency validation
    if (!formData.pricing.currency) {
      errors.push('Currency is required');
    }

    // Fee validation
    if (formData.pricing.cleaningFee && formData.pricing.cleaningFee < 0) {
      errors.push('Cleaning fee cannot be negative');
    } else if (formData.pricing.cleaningFee && formData.pricing.cleaningFee > formData.pricing.basePrice * 2) {
      warnings.push('Cleaning fee seems high compared to base price');
    }

    if (formData.pricing.serviceFee && formData.pricing.serviceFee < 0) {
      errors.push('Service fee cannot be negative');
    }

    if (formData.pricing.taxRate && (formData.pricing.taxRate < 0 || formData.pricing.taxRate > 100)) {
      errors.push('Tax rate must be between 0 and 100 percent');
    }

    // Discount validation
    if (formData.pricing.weeklyDiscount && (formData.pricing.weeklyDiscount < 0 || formData.pricing.weeklyDiscount > 50)) {
      errors.push('Weekly discount must be between 0 and 50 percent');
    }

    if (formData.pricing.monthlyDiscount && (formData.pricing.monthlyDiscount < 0 || formData.pricing.monthlyDiscount > 50)) {
      errors.push('Monthly discount must be between 0 and 50 percent');
    }

    if (formData.pricing.weeklyDiscount && formData.pricing.monthlyDiscount) {
      if (formData.pricing.monthlyDiscount <= formData.pricing.weeklyDiscount) {
        warnings.push('Monthly discount should typically be higher than weekly discount');
      }
    }

    const requiredFields = 2; // basePrice, currency
    const completedFields = [
      formData.pricing.basePrice && formData.pricing.basePrice > 0,
      formData.pricing.currency,
    ].filter(Boolean).length;

    return {
      isComplete: errors.length === 0 && completedFields === requiredFields,
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage: (completedFields / requiredFields) * 100,
    };
  }

  static validateRulesAndPolicies(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check-in/out times
    if (!formData.checkInTime) {
      errors.push('Check-in time is required');
    }

    if (!formData.checkOutTime) {
      errors.push('Check-out time is required');
    }

    // Validate time format and logic
    if (formData.checkInTime && formData.checkOutTime) {
      const checkIn = new Date(`2000-01-01T${formData.checkInTime}`);
      const checkOut = new Date(`2000-01-01T${formData.checkOutTime}`);
      
      if (checkIn >= checkOut) {
        warnings.push('Check-out time should be after check-in time (next day)');
      }
    }

    // Stay requirements
    if (!formData.minimumStay || formData.minimumStay < 1) {
      errors.push('Minimum stay must be at least 1 night');
    } else if (formData.minimumStay > 365) {
      warnings.push('Very long minimum stay may limit bookings');
    }

    if (formData.maximumStay && formData.maximumStay < 1) {
      errors.push('Maximum stay must be at least 1 night');
    }

    if (formData.minimumStay && formData.maximumStay && formData.minimumStay > formData.maximumStay) {
      errors.push('Minimum stay cannot be longer than maximum stay');
    }

    // Cancellation policy
    if (!formData.cancellationPolicy) {
      errors.push('Cancellation policy is required');
    }

    // House rules validation
    if (formData.houseRules && formData.houseRules.length > 10) {
      warnings.push('Too many house rules may discourage bookings');
    }

    const requiredFields = 4; // checkInTime, checkOutTime, minimumStay, cancellationPolicy
    const completedFields = [
      formData.checkInTime,
      formData.checkOutTime,
      formData.minimumStay && formData.minimumStay >= 1,
      formData.cancellationPolicy,
    ].filter(Boolean).length;

    return {
      isComplete: errors.length === 0 && completedFields === requiredFields,
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage: (completedFields / requiredFields) * 100,
    };
  }

  static validatePhotos(formData: ListingFormType): SectionValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!formData.photos || formData.photos.length === 0) {
      warnings.push('At least one photo is highly recommended');
    } else if (formData.photos.length < 3) {
      warnings.push('Adding more photos increases booking likelihood');
    } else if (formData.photos.length > 20) {
      warnings.push('Too many photos may overwhelm guests');
    }

    const hasPhotos = formData.photos && formData.photos.length > 0;
    const completionPercentage = hasPhotos ? 100 : 0;

    return {
      isComplete: true, // Photos are recommended but not required
      isValid: errors.length === 0,
      errors,
      warnings,
      completionPercentage,
    };
  }

  static validateFullForm(formData: ListingFormType): ValidationResult {
    const sections = [
      this.validateBasicInfo(formData),
      this.validateLocation(formData),
      this.validateCapacity(formData),
      this.validateAmenities(formData),
      this.validatePricing(formData),
      this.validateRulesAndPolicies(formData),
      this.validatePhotos(formData),
    ];

    const allErrors = sections.flatMap(section => section.errors);
    const allWarnings = sections.flatMap(section => section.warnings);

    // Convert arrays to objects for compatibility
    const errors: Record<string, string> = {};
    const warnings: Record<string, string> = {};

    allErrors.forEach((error, index) => {
      errors[`error_${index}`] = error;
    });

    allWarnings.forEach((warning, index) => {
      warnings[`warning_${index}`] = warning;
    });

    return {
      isValid: allErrors.length === 0,
      errors,
      warnings,
    };
  }

  static getOverallCompletion(formData: ListingFormType): {
    completionPercentage: number;
    isComplete: boolean;
    sectionsComplete: number;
    totalSections: number;
  } {
    const sections = [
      this.validateBasicInfo(formData),
      this.validateLocation(formData),
      this.validateCapacity(formData),
      this.validateAmenities(formData),
      this.validatePricing(formData),
      this.validateRulesAndPolicies(formData),
      this.validatePhotos(formData),
    ];

    const requiredSections = sections.slice(0, 6); // All except photos
    const sectionsComplete = requiredSections.filter(section => section.isComplete).length;
    const totalSections = requiredSections.length;
    
    const completionPercentage = (sectionsComplete / totalSections) * 100;
    const isComplete = sectionsComplete === totalSections;

    return {
      completionPercentage,
      isComplete,
      sectionsComplete,
      totalSections,
    };
  }
}